<template>
  <v-container class="grey lighten-5" fluid>
    <v-row no-gutters class="pagination-table-header">
      <v-col
        col="12"
      >
        <v-overlay
          :absolute="true"
          :value="isComponentLoading"
        >
          <v-progress-circular indeterminate size="24"></v-progress-circular>
        </v-overlay>
        <v-card
          v-if="L.isEmpty(paginatedObject) || L.isEmpty(paginatedObject.data)"
          class="pa-3">
          <p class="text-center mt-2">
            <v-icon>mdi-account-search-outline</v-icon>
            <b>&nbsp;&nbsp;{{ lang($store, 'sap/listings.cleaners.not_found') }}&nbsp;&nbsp;</b>
            <v-icon>mdi-account-badge-outline</v-icon>
          </p>
        </v-card>
        <div v-else>
          <v-row>
            <v-col
              cols="12"
              md="6"
              lg="4"
              v-for="(item, key) in paginatedObject.data"
              :key="key"
            >
              <v-card
                :color="cardColor(item)"
                class="mx-auto pa-2 mb-2"
                outlined
              >
                <v-list-item
                  :style="{
                   'user-select': 'auto',
                  opacity: (!item.active)?0.5:1,
                }"
                >
                  <v-list-item-content
                    style="position: relative"
                    :style="{color: cardTextColor(item)}"
                  >
                    <div class="overline mb-1">
                      <v-chip
                        v-if="!item.active"
                        class="mr-2 mb-2"
                        :color="cardTextColor(item)"
                        outlined
                        :text-color="cardTextColor(item)"
                      >
                        {{ lang($store, 'sap/listings.global.not_active') }}
                      </v-chip>
                      <div class="d-block d-sm-inline-block">
                        {{ $t('enums.roles.'+ item.role)}}
                        <span
                          v-if="item.rank">
                            ({{ $t('enums.ranks.'+ item.rank)}})
                          </span>
                        <span
                          v-if="item.carier_start_at">
                            [{{ item.carier_start_at.format(DATE_VIEW) }}]
                          </span>
                      </div>
                      <div class="grey--text text-caption mb-2" v-if="item.roles.length">
                        {{$t('cleaners.additional_roles')}}:
                        <span v-for="(role, key) in item.roles" :key="key" class="">
                            /{{$t('enums.roles.'+role)}}/
                          </span>
                      </div>
                    </div>
                    <v-list-item-title
                      class="mb-1"
                      style="white-space: initial; font-size: 18px; line-height: 20px">
                      <small>
                        {{ item.name }}
                      </small>
                      <phone-chip
                        :small="true"
                        :color="cardTextColor(item)"
                        :text-color="cardTextColor(item)"
                        :phone="item.phone"
                      />
                    </v-list-item-title>
                    <v-list-item-subtitle :style="{color: cardTextColor(item)}">
                      {{ item.note }}
                    </v-list-item-subtitle>
                    <div v-if="item.card_number" style="font-size: 14px">
                        <span
                          v-if="item.card_text"
                        >
                          {{ item.card_text }}:
                        </span>
                      <span
                        v-else
                      >
                          {{ $t('cleaners.card') }}:
                        </span>
                      <span style="font-weight: bold;">
                          &nbsp;{{ item.card_number }}
                        </span>
                    </div>
                    <div style="font-size: 14px">
                      {{ $t('cleaners.orders_counter') }}:
                      <span style="font-weight: bold;">
                          &nbsp;{{ item.orders_ordered_done }}
                        </span>
                    </div>
                    <div style="font-size: 14px">
                      {{ $t('cleaners.negatives_counter') }}:
                      <span style="font-weight: bold;">
                          &nbsp;{{ item.negatives }} ({{ item.negatives_percent }}%)
                        </span>
                    </div>
                    <div
                      v-if="item.average_rating > 0"
                      class="d-flex align-center">
                      <star-rating
                        :rating="item.average_rating"
                        :read-only="true"
                        :star-size="20"
                        :increment="0.1"
                        :show-rating="false"
                        inactive-color="#e9e9e9"
                        class="mr-1"
                      />
                      <span>({{ item.average_rating }})</span>
                    </div>
                    <div
                      v-if="item.address"
                      class="mt-1"
                      style="font-size: 13px"
                    >
                      <premise-chip
                        :small="true"
                        :color="cardTextColor(item)"
                        :text-color="cardTextColor(item)"
                      />
                      {{ item.address }}
                    </div>
                    <v-card-actions class="px-0">
                      <is-active
                        :is-ok="item.active"
                        :small="false"
                        :color="cardTextColor(item)"
                        :update-request="updateSimpleRequest(item, 'active')"
                        @updated="item.active = $event; $forceUpdate()"
                      />
                      <v-btn
                        text
                        icon
                        :color="cardTextColor(item)"
                        @click="onItemEdit(item)"
                      >
                        <v-icon>mdi-account-edit-outline</v-icon>
                      </v-btn>
                      <router-link
                        :to="{ name: 'stock-item.show.by_cleaner', params: { uuid: item.uuid }}">
                        <v-icon :title="$t('cleaners.warehouse_history_ico')" :color="cardTextColor(item)">mdi-cart-outline</v-icon>
                      </router-link>
                      <v-btn
                        text
                        icon
                        :color="cardTextColor(item)"
                        @click="onItemDelete(item)"
                      >
                        <v-icon>mdi-trash-can-outline</v-icon>
                      </v-btn>
                      <router-link :to="{ name: item.role === 'cleaner_base' ? 'orders.base' : 'orders.complex', params: { phoneNumber: item.phone }}">
                        <v-icon :title="$t('cleaners.order_ico')" :color="cardTextColor(item)">mdi-clipboard-list-outline</v-icon>
                      </router-link>
                    </v-card-actions>
                    <v-avatar
                      size="60"
                      color="grey lighten-4"
                      v-if="item.avatar"
                      style="position: absolute; bottom: 0; right: 0;"
                      @click="avatarDialogSrc=item.avatar;avatarDialog=true"
                    >
                      <img
                        :src="item.avatar"
                        style="object-fit: cover"
                        alt="Avatar"
                      >
                    </v-avatar>
                  </v-list-item-content>
                </v-list-item>
              </v-card>
            </v-col>
          </v-row>
          <v-dialog
            v-model="avatarDialog"
            style="max-width: 90%;"
          >
            <v-card>
              <v-card-text>
                <div class="pa-3 d-flex justify-center align-center">
                  <img
                    style="max-width: 100%;"
                    :src="avatarDialogSrc">
                </div>
              </v-card-text>

              <v-divider/>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  text
                  @click="avatarDialog = false"
                >
                  Закрыть
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import _ from 'lodash';
/* components */
import PremiseChip from '@/components/minialerts/PremiseChip.vue';
import PhoneChip from '@/components/minialerts/PhoneChip.vue';
import IsActive from '@/components/minialerts/IsActive.vue';
import CleanerProxy from '@/proxies/Cleaner/CleanerProxy';
import StarRating from 'vue-star-rating';
/* constants */
import { MANAGERS } from '@/constants/roles';
import { DATE_VIEW } from '@/constants/dates';
/* utils */
import {
  roles as rolesColors,
  rolesText as rolesTextColors,
} from '../../utils/listingColors';
/* mixins */
import isComponentLoading from '../../mixins/isComponentLoading';
import cardColorsMixin from '../../mixins/cardColorsMixin';

export default {
  name: 'TablePaginatedCleaners',
  mixins: [isComponentLoading, cardColorsMixin],
  components: {
    PremiseChip,
    PhoneChip,
    IsActive,
    StarRating,
  },
  data() {
    return {
      DATE_VIEW,
      MANAGERS,
      rolesColors,
      rolesTextColors,
      L: _,
      avatarDialog: false,
      avatarDialogSrc: '',
    };
  },
  props: {
    paginatedObject: {
      type: Object,
      default: () => {
      },
    },
    noActions: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    account() {
      return this.$store.getters['account/account'];
    },
  },
  methods: {
    onItemEdit(item) {
      this.$emit('edit-item', item);
    },
    onItemDelete(item) {
      this.$emit('delete-item', item);
    },
    updateSimpleRequest(item, property) {
      return value => {
        const data = {};
        data[property] = value;
        return new CleanerProxy().update(item.uuid, data);
      };
    },
  },
};
</script>

<style lang="sass" scoped>
.table-action
  &:hover
    fill: #0066ff
    transition: fill 0.5s
    cursor: pointer

.v-card__actions .v-btn
  min-width: auto
</style>
